import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getEventStartTime,
  getAssetFlightTimesStart,
  getStudioStartTime,
  isCurrentlyLive,
} from '../../../../shared/helpers/assetHelpers';
import { formatTime } from '../../../../helpers/date';
import { formatCalendarTime } from '../../../../utils/datetime';
import { useServerTime } from '../../../utils/useServerTime';
import { SimpleLiveBadge } from '../SimpleLiveBadge/SimpleLiveBadge.jsx';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { FC } from 'react';

import * as styles from './LiveAssetBadge.module.scss';

export type LiveAssetBadgeProps = {
  asset: SvpAssetAdditional;
  dateFormat?: 'time' | 'calendarTime';
};

const LiveAssetBadge: FC<LiveAssetBadgeProps> = ({
  asset,
  dateFormat = 'time',
}) => {
  const { t } = useTranslation();
  const { serverTime } = useServerTime();
  const flightTimeStart = getAssetFlightTimesStart(asset);
  const studioTimeStart = getStudioStartTime(asset);
  const eventTimeStart = getEventStartTime(asset);
  const isLive = isCurrentlyLive(asset, serverTime);

  const formatDate = useCallback(
    (date) => {
      if (dateFormat === 'time') {
        return formatTime(date);
      }

      return formatCalendarTime(date, serverTime);
    },
    [dateFormat, serverTime]
  );

  if (isLive) {
    return <SimpleLiveBadge />;
  }

  if (studioTimeStart) {
    const matchTimeStart = eventTimeStart || flightTimeStart;

    return (
      <div className={styles.container}>
        {studioTimeStart && <span>Studio: {formatDate(studioTimeStart)}</span>}
        {matchTimeStart && (
          <span>
            {t('Match start')}: {formatDate(matchTimeStart)}
          </span>
        )}
      </div>
    );
  }

  return null;
};

export { LiveAssetBadge };
